// Show_Annotations and Show_Values: two independent on/off display preferences shared by every Document_Viewer and
// Enlarged_Viewer. Each is a module-level store read through useSyncExternalStore, so all viewers see one value and a
// change made in the Enlarged_Viewer is already in effect in the Document_Viewer when it closes. The value is kept in
// localStorage as "on" or "off". Unreadable storage or any other stored value gives the default; a write applies to
// the in-memory value first, so a blocked storage write still takes effect for the life of the tab, with no message.
import { useSyncExternalStore } from 'react';

export const SHOW_ANNOTATIONS_KEY = 'bda:showAnnotations';
export const SHOW_VALUES_KEY = 'bda:showValues';

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** The stored setting: true for "on", false for "off", else `fallback` (absent, invalid, no storage, or a throw). */
export function readSetting(storage: ReadableStorage | null, key: string, fallback: boolean): boolean {
  if (!storage) return fallback;
  try {
    const v = storage.getItem(key);
    if (v === 'on') return true;
    if (v === 'off') return false;
    return fallback;
  } catch {
    return fallback;
  }
}

/** window.localStorage, or null when there is no window or accessing it throws (blocked by policy). */
function browserStorage(): WritableStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export interface SettingStore {
  get(): boolean;
  set(v: boolean): void;
  subscribe(listener: () => void): () => void;
  /** Tests: drop the in-memory value so the next read comes from storage again, and stop listening. */
  reset(): void;
}

/** One boolean setting backed by `getStorage()`; exported so tests can inject a failing or fake storage. */
export function createSettingStore(
  key: string,
  fallback: boolean,
  getStorage: () => WritableStorage | null = browserStorage,
): SettingStore {
  let value: boolean | null = null; // null: not read yet
  const listeners = new Set<() => void>();

  const notify = () => listeners.forEach((l) => l());

  const get = () => {
    if (value === null) value = readSetting(getStorage(), key, fallback);
    return value;
  };

  const onStorage = (e: StorageEvent) => {
    // key null means another tab cleared storage.
    if (e.key !== null && e.key !== key) return;
    const next = readSetting(getStorage(), key, fallback);
    if (next !== value) {
      value = next;
      notify();
    }
  };

  const listen = (on: boolean) => {
    if (typeof window === 'undefined') return;
    if (on) window.addEventListener('storage', onStorage);
    else window.removeEventListener('storage', onStorage);
  };

  return {
    get,
    set(v) {
      const changed = get() !== v;
      value = v;
      try {
        getStorage()?.setItem(key, v ? 'on' : 'off');
      } catch {
        // Storage blocked or full: the in-memory value still applies for this tab.
      }
      if (changed) notify();
    },
    subscribe(listener) {
      if (listeners.size === 0) listen(true);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) listen(false);
      };
    },
    reset() {
      if (listeners.size > 0) listen(false);
      listeners.clear();
      value = null;
    },
  };
}

export const showAnnotationsStore = createSettingStore(SHOW_ANNOTATIONS_KEY, true);
export const showValuesStore = createSettingStore(SHOW_VALUES_KEY, false);

function useSetting(store: SettingStore): [boolean, (v: boolean) => void] {
  const v = useSyncExternalStore(store.subscribe, store.get, store.get);
  return [v, store.set];
}

/** Show_Annotations (default on); re-renders on change from any viewer or another tab. */
export function useShowAnnotations(): [boolean, (v: boolean) => void] {
  return useSetting(showAnnotationsStore);
}

/** Show_Values (default off); independent of Show_Annotations, so it is kept while annotations are off. */
export function useShowValues(): [boolean, (v: boolean) => void] {
  return useSetting(showValuesStore);
}

/** Tests: forget both in-memory values so the next read comes from localStorage. */
export function resetAnnotationSettingsForTests(): void {
  showAnnotationsStore.reset();
  showValuesStore.reset();
}
